/**
 * LearnedMappingsTab
 * 
 * Admin UI for reviewing auto-generated job-artifact mappings.
 * Allows promoting learned mappings to manual or rejecting them.
 */

import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { 
  Loader2, 
  CheckCircle2, 
  XCircle, 
  TrendingUp, 
  TrendingDown,
  Sparkles,
  ThumbsUp,
  ThumbsDown,
  RefreshCw
} from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { formatDistanceToNow } from 'date-fns';

interface LearnedMapping {
  id: string;
  artifact_id: string;
  job_id: string;
  mapping_source: string;
  confidence: string;
  relevance_weight: number;
  impression_count: number;
  engagement_count: number;
  last_engaged_at: string | null;
  is_negative: boolean;
  artifact?: {
    title: string;
    provider_name: string | null;
  };
  job?: {
    label: string;
    category_key: string;
  };
}

export function LearnedMappingsTab() {
  const queryClient = useQueryClient();
  const [isProcessing, setIsProcessing] = useState(false);

  // Fetch learned mappings with artifact and job details
  const { data: mappings, isLoading, refetch } = useQuery({
    queryKey: ['learned-mappings'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('training_artifact_job_map')
        .select(`
          id,
          artifact_id,
          job_id,
          mapping_source,
          confidence,
          relevance_weight,
          impression_count,
          engagement_count,
          last_engaged_at,
          is_negative,
          artifact:training_artifacts(title, provider_name),
          job:jobs_catalog(label, category_key)
        `)
        .eq('mapping_source', 'learned')
        .order('engagement_count', { ascending: false });

      if (error) throw error;
      return data as unknown as LearnedMapping[];
    },
  });

  // Promote mapping to manual
  const promoteMutation = useMutation({
    mutationFn: async (mappingId: string) => {
      const { error } = await supabase
        .from('training_artifact_job_map')
        .update({ 
          mapping_source: 'promoted',
          confidence: 'community_validated'
        })
        .eq('id', mappingId);
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success('Mapping promoted');
      queryClient.invalidateQueries({ queryKey: ['learned-mappings'] });
    },
    onError: (error) => {
      toast.error(`Failed to promote: ${error.message}`);
    },
  });

  // Reject (delete) mapping
  const rejectMutation = useMutation({
    mutationFn: async (mappingId: string) => {
      const { error } = await supabase
        .from('training_artifact_job_map')
        .delete()
        .eq('id', mappingId);
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success('Mapping rejected');
      queryClient.invalidateQueries({ queryKey: ['learned-mappings'] });
    },
    onError: (error) => {
      toast.error(`Failed to reject: ${error.message}`);
    },
  });

  // Trigger signal processing
  const handleProcessSignals = async () => {
    setIsProcessing(true);
    try {
      const { data, error } = await supabase.functions.invoke('process-training-signals');
      if (error) throw error;
      
      toast.success(
        `Processed ${data.processed} signals: ${data.created} created, ${data.strengthened} strengthened`
      );
      refetch();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to process signals');
    } finally {
      setIsProcessing(false);
    }
  };

  const getEngagementRate = (impressions: number, engagements: number) => {
    if (impressions === 0) return 0;
    return Math.round((engagements / impressions) * 100);
  };

  return (
    <Card>
      <CardHeader className="pb-4">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-lg flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-primary" />
              Learned Mappings
            </CardTitle>
            <CardDescription>
              Auto-generated job-artifact associations from usage patterns
            </CardDescription>
          </div>
          <Button
            variant="outline"
            onClick={handleProcessSignals}
            disabled={isProcessing}
          >
            {isProcessing ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4 mr-2" />
            )}
            Process Signals
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : !mappings || mappings.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground">
            <Sparkles className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p>No learned mappings yet</p>
            <p className="text-sm mt-2">
              Mappings will appear here as technicians engage with training
            </p>
          </div>
        ) : (
          <ScrollArea className="h-[400px]">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Artifact</TableHead>
                  <TableHead>Job</TableHead>
                  <TableHead className="text-center">Impressions</TableHead>
                  <TableHead className="text-center">Engagements</TableHead>
                  <TableHead className="text-center">Rate</TableHead>
                  <TableHead className="text-center">Weight</TableHead>
                  <TableHead className="text-center">Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {mappings.map((mapping) => {
                  const rate = getEngagementRate(
                    mapping.impression_count,
                    mapping.engagement_count
                  );
                  return (
                    <TableRow key={mapping.id}>
                      <TableCell>
                        <div className="max-w-[200px]">
                          <p className="font-medium truncate">
                            {mapping.artifact?.title || 'Unknown'}
                          </p>
                          <p className="text-xs text-muted-foreground truncate">
                            {mapping.artifact?.provider_name}
                          </p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="max-w-[150px]">
                          <p className="truncate">{mapping.job?.label || 'Unknown'}</p>
                          <p className="text-xs text-muted-foreground">
                            {mapping.job?.category_key}
                          </p>
                        </div>
                      </TableCell>
                      <TableCell className="text-center">
                        {mapping.impression_count}
                      </TableCell>
                      <TableCell className="text-center">
                        {mapping.engagement_count}
                      </TableCell>
                      <TableCell className="text-center">
                        <div className="flex items-center justify-center gap-1">
                          {rate >= 30 ? (
                            <TrendingUp className="h-3 w-3 text-green-500" />
                          ) : rate < 10 ? (
                            <TrendingDown className="h-3 w-3 text-red-500" />
                          ) : null}
                          <span className={rate >= 30 ? 'text-green-600' : rate < 10 ? 'text-red-600' : ''}>
                            {rate}%
                          </span>
                        </div>
                      </TableCell>
                      <TableCell className="text-center">
                        <Badge variant="outline">
                          {(mapping.relevance_weight * 100).toFixed(0)}%
                        </Badge>
                      </TableCell>
                      <TableCell className="text-center">
                        {mapping.is_negative ? (
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger>
                                <XCircle className="h-4 w-4 text-red-500 mx-auto" />
                              </TooltipTrigger>
                              <TooltipContent>
                                Negative: Low engagement despite impressions
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        ) : (
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger>
                                <CheckCircle2 className="h-4 w-4 text-green-500 mx-auto" />
                              </TooltipTrigger>
                              <TooltipContent>
                                Positive association
                                {mapping.last_engaged_at && (
                                  <span className="block text-xs">
                                    Last engaged {formatDistanceToNow(new Date(mapping.last_engaged_at))} ago
                                  </span>
                                )}
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => promoteMutation.mutate(mapping.id)}
                                  disabled={promoteMutation.isPending}
                                >
                                  <ThumbsUp className="h-4 w-4 text-green-600" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>Promote to validated</TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => rejectMutation.mutate(mapping.id)}
                                  disabled={rejectMutation.isPending}
                                >
                                  <ThumbsDown className="h-4 w-4 text-red-600" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>Reject mapping</TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </ScrollArea>
        )}
      </CardContent>
    </Card>
  );
}
